import { useState } from "react";
import { Link } from "react-router";

import { Notice, Page } from "@/components/page";
import { StatusBadge } from "@/components/status";
import { control, subText, table, td, th } from "@/components/table";
import { type OffboardingStatus, type OffboardingSummary, useOffboardings } from "@/lib/api";
import { errorMessage, inZone } from "@/lib/format";

/**
 * Offboarding queue — what is still live, and everything that has happened.
 *
 * Two sections, because they answer two different questions and only one of
 * them is urgent. **Open** is work in flight: scheduled, running, half-applied,
 * waiting to be put back. **History** is the record: completed, reverted,
 * cancelled, failed, re-enabled. Mixing them buries a `partial` — the state
 * that costs somebody their morning — among months of finished rows, and the
 * one thing this screen has to do well is make a half-applied offboarding
 * impossible to miss. That is also why `partial` and `failed` are the only
 * solid pills.
 *
 * Every row carries its own outcome, so the history is readable as a list of
 * what was decided rather than a list of names.
 *
 * Nothing on this screen executes anything, and it does not explain the
 * backend's state either: how the worker is configured is an operations
 * question, answered on /admin rather than on a screen HR reads every day.
 *
 * A row links to `/leaver/:id`, which is the one place a record is read and
 * acted on. The queue does not carry a second, shallower copy of that screen.
 */

const STATUS_FILTERS: Array<{ value: "" | OffboardingStatus; label: string }> = [
  { value: "", label: "All statuses" },
  { value: "scheduled", label: "Scheduled" },
  { value: "running", label: "Running" },
  { value: "phase1_done", label: "Phase 1 done" },
  { value: "partial", label: "Partial" },
  { value: "reinstating", label: "Re-enabling" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "reverted", label: "Reverted" },
  { value: "cancelled", label: "Cancelled" },
  { value: "reinstated", label: "Re-enabled" },
];

/**
 * Still in flight. Anything else is history.
 *
 * `partial` and `failed` differ here and it is deliberate: a partial is
 * retried automatically and is therefore still live, while a failed record
 * applied nothing and is finished. `reinstating` is open because somebody is
 * locked out of their work until it clears.
 */
const OPEN_STATUSES: OffboardingStatus[] = [
  "scheduled",
  "running",
  "phase1_done",
  "partial",
  "reinstating",
];

function isOpen(item: OffboardingSummary): boolean {
  return OPEN_STATUSES.includes(item.status);
}

export function QueuePage() {
  const [status, setStatus] = useState<"" | OffboardingStatus>("");
  const { data, isLoading, error } = useOffboardings(status || null);

  const items = data?.items ?? [];
  const open = items.filter(isOpen);
  const history = items.filter((item) => !isOpen(item));

  return (
    <Page
      title="Offboarding queue"
      intro="Every leaver HROps knows about. Open is what is still in flight; History is what was decided, and by whom."
      actions={
        <label className="text-fg-muted flex items-center gap-2 text-xs">
          Status
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as "" | OffboardingStatus)}
            className={control}
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {isLoading ? (
        <Notice>Loading the queue…</Notice>
      ) : error ? (
        <Notice tone="danger">{errorMessage(error, "Could not load the queue.")}</Notice>
      ) : items.length === 0 ? (
        <Notice>
          {status
            ? `Nothing in the queue with status “${status}”.`
            : "Nothing is scheduled. No leaver has been recorded yet."}
        </Notice>
      ) : status ? (
        // A chosen filter is already one question; splitting the answer in two
        // would only hide half of it under a heading.
        <Section
          heading={`Status “${status}”`}
          note={`${items.length} record${items.length === 1 ? "" : "s"}.`}
          rows={items}
        />
      ) : (
        <>
          <Section
            heading="Open"
            note={
              open.length === 0
                ? "Nothing in flight. Every record has run its course."
                : "Scheduled, running, half-applied, or waiting to be re-enabled."
            }
            rows={open}
          />
          <Section
            heading="History"
            note={
              history.length === 0
                ? "Nothing has finished yet."
                : "Everything that has finished, with what was decided."
            }
            rows={history}
          />
        </>
      )}
    </Page>
  );
}

function Section({
  heading,
  note,
  rows,
}: {
  heading: string;
  note: string;
  rows: OffboardingSummary[];
}) {
  return (
    <section className="mt-8 first:mt-6">
      <h2 className="text-fg text-sm font-semibold">
        {heading}
        <span className="text-fg-muted ml-2 font-normal tabular-nums">{rows.length}</span>
      </h2>
      <p className={`${subText} mt-1`}>{note}</p>
      {rows.length === 0 ? null : (
        <div className="mt-3 overflow-x-auto">
          <table className={table}>
            <caption className="sr-only">
              {heading}: recorded offboardings, newest first. Select a name to open the record.
            </caption>
            <thead>
              <tr>
                <th scope="col" className={th}>
                  Subject
                </th>
                <th scope="col" className={th}>
                  Effective
                </th>
                <th scope="col" className={th}>
                  Status
                </th>
                <th scope="col" className={th}>
                  Requester
                </th>
                <th scope="col" className={th}>
                  Delegate
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                const when = inZone(item.effective_at, item.local_tz);
                return (
                  <tr key={item.id} className="hover:bg-bg-subtle">
                    <td className={td}>
                      <Link to={`/leaver/${item.id}`} className="text-fg font-medium underline">
                        {item.subject_display_name ?? item.subject_upn}
                      </Link>
                      {item.subject_display_name ? (
                        <div className={subText}>{item.subject_upn}</div>
                      ) : null}
                    </td>
                    <td className={`${td} whitespace-nowrap`}>
                      <div className="text-fg tabular-nums">{when.time}</div>
                      <div className={subText}>{when.zone}</div>
                    </td>
                    <td className={td}>
                      <StatusBadge status={item.status} />
                      {/* What was decided, not just where it ended up. In a
                          history list the reason is the content. */}
                      <Outcome item={item} />
                    </td>
                    <td className={td}>
                      <div className="text-fg">{item.requester_upn}</div>
                      <div className={subText}>{item.requester_role}</div>
                    </td>
                    <td className={td}>
                      {item.delegate_to_upn ? (
                        <>
                          <div className="text-fg">{item.delegate_to_upn}</div>
                          <div className={subText}>
                            {item.grant_send_as ? "with Send As" : "no Send As"}
                          </div>
                        </>
                      ) : (
                        <span className={subText}>no handover</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The one line under a status that says who ended it, and when. */
function Outcome({ item }: { item: OffboardingSummary }) {
  const ended =
    item.reinstated_at ?? item.reinstate_requested_at ?? item.reverted_at ?? item.cancelled_at;
  if (!ended) {
    return null;
  }
  const who = item.reinstate_requested_by ?? item.reverted_by ?? item.cancelled_by;
  const what = item.reinstate_requested_at
    ? item.reinstated_at
      ? "re-enabled"
      : "re-enable asked for"
    : item.reverted_at
      ? "reverted"
      : "cancelled";
  return (
    <div className={subText}>
      {what} {inZone(ended, item.local_tz).time}
      {who ? ` by ${who}` : ""}
    </div>
  );
}
